/** Value + fetcher types for the reusable tag-condition widget. */

/** How a chip is stored — three shapes, discriminated by key presence:
 *
 *  - `{tag_id}` — pinned to one concrete tag;
 *  - `{category_code, tag_name}` — the tag LITERALLY named this, resolved
 *    against the rendering tenant at evaluation time;
 *  - `{category_code, canonical}` — every tag name `Tag_Name_Alias` normalises
 *    to that token, plus the token itself.
 *
 *  The third exists because names vary where identity does not: production
 *  carries 60+ spellings of "Viator" in one category, so a chip naming one of
 *  them misses the rest. Only this shape consults the alias table. */
export type TagSpec =
  | { tag_id: number }
  | { category_code: string; tag_name: string }
  | { category_code: string; canonical: string };

/** Narrowing helper — `canonical` is the discriminant the backend uses too. */
export function isCanonicalSpec(
  spec: TagSpec,
): spec is { category_code: string; canonical: string } {
  return 'canonical' in spec;
}

/** Display strings that ride along with the spec (no side-channel lookups).
 *  `tenant` is null for system-wide specs (and for pinned system tags). */
export interface TagChipLabels {
  tenant: string | null;
  category: string;
  tag: string;
}

export interface TagChip {
  spec: TagSpec;
  labels: TagChipLabels;
}

export interface TagConditionValue {
  /** and = all chips must match, or = any chip. */
  matchMode: 'and' | 'or';
  chips: TagChip[];
}

export const EMPTY_TAG_CONDITION: TagConditionValue = { matchMode: 'and', chips: [] };

/** Local sentinel for the tenant stage — "no tenant" routes the builder onto
 *  the system-wide (by-code) path. Deliberately NOT the notifications
 *  feature's SYSTEM_TENANT: this widget is feature-agnostic. */
export const SYSTEM_WIDE = { id: 0, name: 'System-wide (any tenant)' };

// ---------------------------------------------------------------------------
// Injected fetchers — the widget never imports API modules; the host feature
// binds these to whatever endpoints its users are permitted to call.
// ---------------------------------------------------------------------------

export interface TenantCategoryOption {
  id: number;
  code: string;
  name: string;
}

export interface CategoryCodeOption {
  code: string;
  name: string | null;
}

export interface CategoryTagOption {
  id: number;
  name: string;
}

export interface TagNameOption {
  name: string;
}

export interface TagConditionFetchers {
  /** Categories owned by one tenant (by-id chip path). */
  fetchTenantCategories: (
    tenantId: number,
    q: string,
    signal: AbortSignal,
  ) => Promise<TenantCategoryOption[]>;
  /** Category codes across all tenants, deduped (system-wide chip path). */
  fetchAllCategoryCodes: (q: string, signal: AbortSignal) => Promise<CategoryCodeOption[]>;
  /** Tags of one concrete category (by-id chip path). */
  fetchCategoryTags: (
    categoryId: number,
    q: string,
    signal: AbortSignal,
  ) => Promise<CategoryTagOption[]>;
  /** Tag names for a category code across all tenants, deduped (system-wide
   *  chip path; an unknown code just yields zero suggestions — free text). */
  fetchTagsForCode: (
    categoryCode: string,
    q: string,
    signal: AbortSignal,
  ) => Promise<TagNameOption[]>;
  /** Canonical tokens declared for a category code (canonical chip path).
   *
   *  Optional: a host that does not supply it simply does not offer the
   *  canonical path, which is how the notification `tag` block — a reference to
   *  ONE tag — opts out of a shape that matches many by design. */
  fetchCanonicalsForCode?: (
    categoryCode: string,
    q: string,
    signal: AbortSignal,
  ) => Promise<CanonicalOption[]>;
}

export interface CanonicalOption {
  canonical: string;
  /** How many aliases currently normalise to it — the "is this live?" signal. */
  alias_count?: number | null;
}
